import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Experiment {
  id: string
  name: string
  description: string
  status: string
  type: string
  traffic_allocation: number
  variants: { id: string; name: string; traffic: number; conversions: number; visitors: number }[]
  goal: string
  winner?: string
}

export default function ABTestingPage() {
  const [experiments, setExperiments] = useState<Experiment[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [selectedExperiment, setSelectedExperiment] = useState<Experiment | null>(null)
  const [results, setResults] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [expRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/ab-testing/experiments'),
      fetch(API_BASE + '/ab-testing/statistics')
    ])
    if (expRes.ok) setExperiments((await expRes.json()).experiments || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const viewResults = async (exp: Experiment) => {
    setSelectedExperiment(exp)
    const res = await fetch(API_BASE + '/ab-testing/experiments/' + exp.id + '/results')
    if (res.ok) setResults(await res.json())
  }

  const startExperiment = async (id: string) => {
    await fetch(API_BASE + '/ab-testing/experiments/' + id + '/start', { method: 'POST' })
    fetchData()
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running': return 'bg-green-100 text-green-800'
      case 'completed': return 'bg-blue-100 text-blue-800'
      case 'draft': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">A/B Testing Framework</h1>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Experiments</div>
              <div className="text-2xl font-bold">{statistics.total_experiments}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Running</div>
              <div className="text-2xl font-bold text-green-600">{statistics.running}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Completed</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.completed}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Draft</div>
              <div className="text-2xl font-bold text-gray-500">{statistics.draft}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Lift</div>
              <div className="text-2xl font-bold text-purple-600">+{statistics.avg_lift}%</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-semibold">Experiments</h2>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Experiment</button>
            </div>
            <div className="divide-y">
              {experiments.map(exp => (
                <div key={exp.id} className="p-4 hover:bg-gray-50 cursor-pointer" onClick={() => viewResults(exp)}>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-semibold">{exp.name}</div>
                      <div className="text-sm text-gray-500">{exp.description}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(exp.status)}`}>{exp.status}</span>
                      {exp.status === 'draft' && (
                        <button onClick={(e) => { e.stopPropagation(); startExperiment(exp.id); }} className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700">Start</button>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-4 text-xs text-gray-500">
                    <span>Goal: {exp.goal}</span>
                    <span>Traffic: {exp.traffic_allocation}%</span>
                    <span>Variants: {exp.variants.length}</span>
                  </div>
                  {exp.winner && <div className="mt-2 text-sm text-green-600">Winner: {exp.variants.find(v => v.id === exp.winner)?.name}</div>}
                </div>
              ))}
            </div>
          </div>

          <div>
            {selectedExperiment && results ? (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">{selectedExperiment.name} Results</h3>
                <div className="space-y-4">
                  {results.results?.map((r: any) => (
                    <div key={r.variant_id} className={`p-4 rounded-lg ${results.winner === r.variant_id ? 'bg-green-50 border-2 border-green-500' : 'bg-gray-50'}`}>
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium">{r.name}</span>
                        {results.winner === r.variant_id && <span className="text-xs bg-green-600 text-white px-2 py-1 rounded">Winner</span>}
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><span className="text-gray-500">Visitors:</span> {r.visitors.toLocaleString()}</div>
                        <div><span className="text-gray-500">Conversions:</span> {r.conversions}</div>
                        <div><span className="text-gray-500">Rate:</span> {r.conversion_rate}%</div>
                        <div><span className="text-gray-500">Confidence:</span> {r.confidence?.toFixed(1)}%</div>
                      </div>
                    </div>
                  ))}
                </div>
                {results.statistical_significance && <div className="mt-4 text-sm text-green-600">Statistically significant</div>}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Experiment Results</h3>
                <p className="text-gray-500 text-sm">Select an experiment to view results</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
